import { useEntities } from "@app/store/entityStore";
import {
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    Table,
} from "@design-system/Table";
import { useAtomValue } from "jotai";
import {
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
    type FC,
} from "react";
import { useParams } from "react-router-dom";
import { StringParam, useQueryParam } from "use-query-params";
import { useObjectivesWithDetails } from "../../data";
import { useFilteredObjectives } from "../../hooks";
import { useColumns } from "./columns";
import {
    selectedSubthemesAtomFamily,
    selectedThemesAtomFamily,
    selectedUsersAtomFamily,
} from "../../ctx";
import { EmptySearchState } from "./EmptySearchState";
import { type Objective, type TableProps } from "./types";

export const UngroupedTable: FC<TableProps> = ({
    entityId,
    onSelectionChange,
    ref,
}) => {
    const { entities } = useEntities();
    const columns = useColumns();
    const { objectives, isPending } = useObjectivesWithDetails(
        {
            entityId,
        },
        { skip: entities?.length != 0 && !entityId },
    );
    const [urlSearchString] = useQueryParam("search", StringParam);
    const { org_uname } = useParams();
    const selectedThemesIds = useAtomValue(selectedThemesAtomFamily(org_uname));

    const selectedSubthemesIds = useAtomValue(
        selectedSubthemesAtomFamily(org_uname),
    );

    const selectedUserIds = useAtomValue(selectedUsersAtomFamily(org_uname));

    const [rowSelection, setRowSelection] = useState({});

    useImperativeHandle(ref, () => ({
        unset: () => setRowSelection({}),
    }));

    const objectivesFiltered = useFilteredObjectives(
        objectives || [],
        urlSearchString || "",
    );

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const columnFilters = useMemo(() => {
        const filters = [];

        if (selectedThemesIds.length) {
            filters.push({ id: "theme", value: selectedThemesIds });
        }

        if (selectedSubthemesIds.length) {
            filters.push({ id: "subtheme", value: selectedSubthemesIds });
        }

        if (selectedUserIds.length) {
            filters.push({ id: "owner", value: selectedUserIds });
        }

        return filters;
    }, [selectedThemesIds, selectedSubthemesIds, selectedUserIds]);

    const tableOptions = {
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onRowSelectionChange: setRowSelection,
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange: setPagination,
        getRowId: (row: Objective) => row.id,
        state: {
            rowSelection,
            pagination,
            columnFilters,
        },
        autoResetPageIndex: false,
    };

    useEffect(() => {
        if (!isPending && objectives) {
            setPagination({
                pageIndex: 0,
                pageSize: 10,
            });
        }
    }, [objectivesFiltered, urlSearchString, columnFilters]);

    useEffect(() => {
        const selectedObjectives = Object.keys(rowSelection)
            .filter((k) => rowSelection[k as keyof typeof rowSelection])
            .filter((id) =>
                objectivesFiltered.find((objective) => objective.id === id),
            )
            .filter((elt) => elt);
        onSelectionChange(selectedObjectives);
    }, [rowSelection]);

    return (
        <Table
            columns={columns}
            data={objectivesFiltered ?? []}
            options={tableOptions}
            loading={isPending}
            EmptyBody={EmptySearchState}
            variant="grid"
        />
    );
};
